import * as ACTIONS from '@/store/constants/actions.js';
import * as GETTERS from '@/store/constants/getters.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import {
    createQueue,
    deleteQueue,
    createBatch,
    deleteBatch,
    batchIsActive,
    updateQueue,
} from '@/generated/console.js';
import {
    getUniqueUsers,
    fetchDisabledContent,
    fetchAgreementRate,
    fetchUsers,
    commitDisabledRatios,
    commitAgreementRate,
    formatFiltersFromApi,
    formatFiltersForApi,
} from '../../helpers/moderation_list.js';

export const actions = {
    [ACTIONS.CREATE_QUEUE]({ commit, getters }, data) {
        data.workspace_uuid = getters[GETTERS.CURRENT_WORKSPACE_UUID];
        return createQueue({ queue: formatRequest(data) }).then((response) => {
            commit(MUTATIONS.SET_QUEUE, {
                key: response.queue_uuid,
                item: {
                    ...response,
                    filters: formatFiltersFromApi(response.filters, getters),
                },
            });
            commit(MUTATIONS.ADD_QUEUE_TO_WORKSPACE, response);
            return response;
        });
    },
    [ACTIONS.FETCH_QUEUE]({ commit, getters, dispatch }, queue_uuid) {
        return createBatch({
            createBatchRequest: {
                queue_uuid,
            },
        }).then((response) => {
            commit(MUTATIONS.SET_QUEUE, {
                key: response.queue_uuid,
                item: {
                    ...response.queue,
                    filters: formatFiltersFromApi(response.queue.filters, getters),
                    content: [],
                    batch_uuid: '',
                    expiry_date: response.expiry_date,
                    unsaved_content: [],
                    tag_list: response.tag_list,
                    filter_tag_list: response.filter_tag_list,
                },
            });

            const uniqueUsers = getUniqueUsers(response.content);

            if (uniqueUsers.length > 0) {
                // get the disabled content ratios
                // The queue doesn't need to wait for this
                fetchDisabledContent(uniqueUsers)
                    .then(({ disabled_content_ratios }) => commitDisabledRatios(disabled_content_ratios, commit));

                // get the agreement rate
                // The queue doesn't need to wait for this either
                fetchAgreementRate(uniqueUsers)
                    .then(({ users }) => commitAgreementRate(users, commit));
            }

            // Hit the workspaces endpoint so that the numbers update
            dispatch(ACTIONS.FETCH_WORKSPACES_INFORMATION);
            commit(MUTATIONS.SET_QUEUE, {
                key: response.queue_uuid,
                item: {
                    batch_uuid: response.batch_uuid,
                },
            });
            // Don't want to wait for users to be fetched cuz then one broken user will take down the whole search
            fetchUsers(uniqueUsers, dispatch);

            return Promise.all(response.content.map((item) =>
                dispatch(ACTIONS.SET_FETCHED_CONTENT, item)
                    .then((content_id) => {
                        commit(MUTATIONS.ADD_CONTENT_TO_QUEUE, {
                            queue_uuid,
                            content_id,
                        });
                        return Promise.resolve();
                    }),
            )).then(() => Promise.resolve(response));
        });
    },
    [ACTIONS.RENAME_QUEUE]({ commit, dispatch, getters }, { queue_uuid, name }) {
        const params = {
            queue: {
                ...getters[GETTERS.QUEUE_BY_UUID](queue_uuid),
                name,
            },
            queue_uuid,
        };

        return dispatch(ACTIONS.UPDATE_QUEUE, params).then(() => {
            commit(MUTATIONS.SET_QUEUE_PROPERTY, {
                key: queue_uuid,
                item: { name },
            });
            return Promise.resolve();
        });
    },
    [ACTIONS.EDIT_QUEUE]() { return; },
    [ACTIONS.DELETE_QUEUE]({ commit }, { queue_uuid }) {
        return deleteQueue({ queue_uuid }).then(() => {
            commit(MUTATIONS.REMOVE_QUEUE, queue_uuid);
            return Promise.resolve();
        });
    },
    [ACTIONS.DELETE_BATCH](store, { batch_uuid }) {
        return deleteBatch({ batch_uuid });
    },
    [ACTIONS.BATCH_IS_ACTIVE]({ commit }, { batch_uuid }) {
        return batchIsActive({ batch_uuid }).then((response) => {
            // don't override content. It doesn't come back from the api
            delete response.content;

            commit(MUTATIONS.SET_QUEUE, {
                key: response.queue_uuid,
                item: response,
            });
        });
    },

    [ACTIONS.UPDATE_QUEUE]({}, { queue_uuid, queue }) { // eslint-disable-line no-empty-pattern
        return updateQueue({
            queue_uuid,
            queue: formatRequest(queue),
        });
    },
};

const formatRequest = (data) => ({
    workspace_uuid: data.workspace_uuid,
    name: data.name,
    note: data.note || '',
    filters: formatFiltersForApi(data.filters),
    sla_millis: (data.sla || 0) * 60 * 1000,
    client_uuid: data.client_uuid,
});
